// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect } from 'react';

import type { RenderableUser, UserId } from '@evolvemep/api-ts';
import { Flex, Avatar, Loader, type MantineSize, type FlexProps } from '@mantine/core';

import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import type { RequireAtLeastOne } from 'helpers/helper-types';
import { validateUserNameOrEmail } from 'helpers/userHelpers';
import { useUsersInfo } from 'hooks/user/useUsersInfo';

type Props = {
  hideTooltip?: boolean;
  /** @defaultValue 20 */
  size?: MantineSize | string | number;
  /** @defaultValue="sm" */
  fz?: FlexProps['fz'];
} & RequireAtLeastOne<{
  user?: RenderableUser;
  userId?: UserId;
}>;

export const UserAvatar = ({ size, name }: Pick<Props, 'size'> & { name: string }) => (
  <Avatar
    size={size}
    name={name}
    color="initials"
    variant="light-mantine"
    styles={{
      placeholder: { fontSize: 10 },
    }}
  />
);

/** Prefer `UserInfoBadge` instead */
export const UserInfo = ({ user: userFromParent, userId, hideTooltip, size = 20, fz = 'sm' }: Props) => {
  const { fetchUserInfo, userInfo } = useUsersInfo();

  useEffect(() => {
    if (userId) {
      void fetchUserInfo(userId);
    }
  }, [fetchUserInfo, userId]);

  const user = userFromParent
    ? {
        completeName: validateUserNameOrEmail(userFromParent),
        email: userFromParent.userEmail,
      }
    : userId
      ? userInfo[userId]
      : null;

  if (!user) return <Loader size="xs" type="dots" />;

  return (
    <AgGridStyleTooltip label={user.email} openDelay={250} hidden={hideTooltip} position="top-start">
      <Flex gap="xs" fz={fz} align="center">
        <UserAvatar size={size} name={user.completeName} />
        {/*<Text lineClamp={1}>*/}
        {user.completeName}
        {/*</Text>*/}
      </Flex>
    </AgGridStyleTooltip>
  );
};
